import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { useProjects } from '@/api/hooks'
import { ProjectsTable, ProjectsTableSkeleton } from '@/components/projects-table'
import { Button } from '@/components/ui/button'

import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { SettingCell } from '@/components/setting-cell'
import { StatusPill } from '@/components/status-pill'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney, parseUTCDate } from '@/lib/utils'
import { sentenceCase as sentence, type StatusTone } from '@/lib/status-tones'

import { useUpdateBankPaymentPolicy, type BankPaymentPolicy, type CustomerPayments } from './api'

type CustomerProject = CustomerPayments['projects'][number]
type CustomerInvoice = CustomerPayments['invoices'][number]
type CustomerPayment = CustomerPayments['payments'][number]

const PROJECTS_PER_PAGE = 20


const POLICY_CHOICES = [
  { value: 'automatic', label: 'Automatic' },
  { value: 'proceed', label: 'Start right away' },
  { value: 'wait', label: 'Wait to clear' },
] as const satisfies ReadonlyArray<{ value: BankPaymentPolicy; label: string }>

const PROJECT_PAYMENT: Record<CustomerProject['payment_status'], { label: string; tone: StatusTone }> = {
  paid: { label: 'Paid', tone: 'success' },
  partially_paid: { label: 'Partly paid', tone: 'warning' },
  refunded: { label: 'Refunded', tone: 'neutral' },
  unpaid: { label: 'Unpaid', tone: 'warning' },
  no_order: { label: 'No order', tone: 'neutral' },
}

const PAYMENT_LABELS: Record<string, string> = {
  payment: 'Paid online',
  payment_out_of_band: 'Payment recorded',
  external_payment: 'Payment recorded',
  payment_processing: 'Bank payment processing',
  payment_failed: 'Bank payment failed',
  refund: 'Refunded',
}

const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const formatDay = (value: string | null | undefined) => (value ? day.format(parseUTCDate(value)) : '—')

/** Invoiced, paid, owed and the bank payment setting, atop the customer's Payments tab (USA-1283). */
export function CustomerPaymentsGlance({ uuid, payments }: { uuid: string; payments: CustomerPayments }) {
  const update = useUpdateBankPaymentPolicy(uuid)
  const repeat = payments.paid_projects > 0
  const automatic =
    payments.bank_payment_policy === 'automatic'
      ? repeat
        ? 'Repeat customer: starts right away'
        : 'First-time customer: waits to clear'
      : null
  return (
    <section aria-label="Invoice totals" className="overflow-hidden rounded-xl border bg-card">
      <div className="-mb-px -mr-px grid grid-cols-3 lg:grid-cols-[1fr_1fr_1fr_minmax(0,2fr)]">
        <SettingCell label="Invoiced">
          <span className="text-sm font-semibold tabular-nums">{formatMoney(payments.invoiced)}</span>
        </SettingCell>
        <SettingCell label="Paid">
          <span className="text-sm font-semibold tabular-nums">{formatMoney(payments.paid)}</span>
        </SettingCell>
        <SettingCell label="Owed">
          <span className="text-sm font-semibold tabular-nums">{formatMoney(payments.owed)}</span>
        </SettingCell>
        <SettingCell
          className="col-span-3 lg:col-span-1"
          label="Bank payments before they clear"
          below={automatic ? <p className="mt-1 text-xs text-muted-foreground">{automatic}</p> : null}
        >
          <ReviewChoiceGroup
            label="Bank payments before they clear"
            onChange={(policy) => {
              if (policy !== payments.bank_payment_policy) update.mutate(policy)
            }}
            options={POLICY_CHOICES}
            value={payments.bank_payment_policy}
          />
        </SettingCell>
      </div>
    </section>
  )
}

/** The customer's projects in the Projects page's table, each with how it was paid (USA-1305). */
export function CustomerProjects({ customerGroupId, projects }: { customerGroupId: number; projects: CustomerProject[] }) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useProjects({ customer_group_id: customerGroupId, page, limit: PROJECTS_PER_PAGE })
  const paid = new Map(projects.map((project) => [project.uuid, PROJECT_PAYMENT[project.payment_status]]))
  if (isLoading) return <ProjectsTableSkeleton rows={4} />
  if (!data?.items.length) {
    return <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No projects yet</p>
  }
  const pages = data.pages ?? 1
  return (
    <div className="space-y-3">
      <ProjectsTable
        items={data.items}
        payment={(project) => {
          const state = paid.get(project.uuid)
          return state ? <StatusPill tone={state.tone}>{state.label}</StatusPill> : null
        }}
        showCustomer={false}
      />
      {pages > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Page {data.page} of {pages}
          </p>
          <div className="flex gap-2">
            <Button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} size="sm" variant="outline">
              <ChevronLeft className="h-4 w-4" />
              <span className="max-sm:sr-only">Previous</span>
            </Button>
            <Button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} size="sm" variant="outline">
              <span className="max-sm:sr-only">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function invoiceState(invoice: CustomerInvoice): { label: string; tone: StatusTone } {
  if (invoice.status === 'paid') return { label: 'Paid', tone: 'success' }
  if (invoice.status === 'void') return { label: 'Void', tone: 'neutral' }
  if (invoice.status === 'uncollectible') return { label: 'Uncollectible', tone: 'danger' }
  if (invoice.payment_processing_at) return { label: 'Bank payment processing', tone: 'progress' }
  if (invoice.due_at && parseUTCDate(invoice.due_at) < new Date()) return { label: 'Overdue', tone: 'danger' }
  return { label: 'Open', tone: 'progress' }
}

export function CustomerInvoicesTable({ invoices }: { invoices: CustomerInvoice[] }) {
  if (!invoices.length) {
    return <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No invoices yet</p>
  }
  return (
    <>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Quote</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Issued</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const state = invoiceState(invoice)
              return (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Link
                      className="font-mono font-medium text-primary hover:underline"
                      params={{ invoiceId: invoice.id }}
                      to="/receivables/$invoiceId"
                    >
                      {invoice.number ?? 'Invoice'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {invoice.quote ? (
                      <Link
                        className="text-primary hover:underline"
                        params={{ quoteId: invoice.quote.id }}
                        to="/quotes/$quoteId"
                      >
                        {invoice.quote.quote_number}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusPill tone={state.tone}>{state.label}</StatusPill>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(invoice.amount_due)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(invoice.amount_paid)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatDay(invoice.finalized_at)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <ul className="space-y-2 md:hidden">
        {invoices.map((invoice) => {
          const state = invoiceState(invoice)
          return (
            <li className="rounded-lg border bg-card p-3" key={invoice.id}>
              <div className="flex items-start justify-between gap-2">
                <Link
                  className="font-mono text-sm font-medium text-primary hover:underline"
                  params={{ invoiceId: invoice.id }}
                  to="/receivables/$invoiceId"
                >
                  {invoice.number ?? 'Invoice'}
                </Link>
                <StatusPill tone={state.tone}>{state.label}</StatusPill>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatMoney(invoice.amount_due)} · paid {formatMoney(invoice.amount_paid)} · {formatDay(invoice.finalized_at)}
              </p>
            </li>
          )
        })}
      </ul>
    </>
  )
}

export function CustomerPaymentsList({ payments }: { payments: CustomerPayment[] }) {
  if (!payments.length) {
    return <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No payments yet</p>
  }
  return (
    <ul className="divide-y rounded-lg border bg-card">
      {payments.map((entry) => (
        <li className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" key={entry.id}>
          <span className="min-w-0">
            <span className={entry.entry_type === 'payment_failed' ? 'block font-medium text-destructive' : 'block font-medium'}>
              {entry.removed_at ? 'Payment removed' : (PAYMENT_LABELS[entry.entry_type] ?? sentence(entry.entry_type))}
              {entry.method ? ` · ${sentence(entry.method)}` : ''}
            </span>
            <span className="block text-xs text-muted-foreground">
              {formatDay(entry.occurred_at)}
              {entry.reference ? ` · ${entry.reference}` : ''}
            </span>
          </span>
          <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
            {entry.entry_type === 'refund' ? `−${formatMoney(entry.amount)}` : formatMoney(entry.amount)}
          </span>
        </li>
      ))}
    </ul>
  )
}
